/**
 * Tiny synthesized sounds (Web Audio). No audio files → nothing to download,
 * no licensing. Browsers only allow sound after the first user tap, so the
 * context is created lazily on the first play() call (which is always a tap).
 */
import { ambient } from './ambient';

let ctx: AudioContext | null = null;
let enabled = true;

function ac(): AudioContext | null {
  if (!enabled) return null;
  try {
    ctx ??= new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    if (ctx.state === 'suspended') void ctx.resume().then(() => ambient.attach(ctx!));
    else ambient.attach(ctx);
    return ctx;
  } catch { return null; }
}

/**
 * iOS Safari only lets audio start inside a user gesture, and sometimes keeps the context
 * suspended after creation. Call this on early taps: it resumes the context and plays one
 * silent sample, after which every later sound plays immediately.
 */
function unlock(): boolean {
  const a = ac();
  if (!a) return false;
  try {
    const buf = a.createBuffer(1, 1, 22050);
    const src = a.createBufferSource();
    src.buffer = buf; src.connect(a.destination); src.start(0);
  } catch { /* ignore */ }
  return a.state === 'running';
}

/** Keep trying on the first few taps until audio is running, then stop listening. */
export function installAudioUnlock(): void {
  const handler = () => {
    if (unlock()) ['pointerdown', 'touchend', 'click'].forEach(ev => document.removeEventListener(ev, handler, true));
  };
  ['pointerdown', 'touchend', 'click'].forEach(ev => document.addEventListener(ev, handler, { capture: true, passive: true }));
}

// ---------------------------------------------------------------- music box
// A soft music-box voice (sine + a few bell partials, quick attack, long fade) through a
// small warm echo, so taps sound like a toy music box playing in another room.
let bus: AudioNode | null = null;
function musicBus(a: AudioContext): AudioNode {
  if (bus) return bus;
  const master = a.createGain(); master.gain.value = 0.9; master.connect(a.destination);
  const delay = a.createDelay(1); delay.delayTime.value = 0.23;
  const fb = a.createGain(); fb.gain.value = 0.28;
  const tone = a.createBiquadFilter(); tone.type = 'lowpass'; tone.frequency.value = 2600;
  const send = a.createGain(); send.gain.value = 0.32;
  const input = a.createGain();
  input.connect(master);
  input.connect(send).connect(delay).connect(tone).connect(fb).connect(delay);
  tone.connect(master);
  bus = input;
  return bus;
}

function bell(freq: number, start = 0, vol = 0.16, len = 1.4) {
  const a = ac(); if (!a) return;
  const out = musicBus(a);
  const t = a.currentTime + start;
  // fundamental, octave and a slightly inharmonic "tine" partial that dies fast
  ([[1, 1, len], [2, 0.32, len * 0.6], [4.16, 0.12, 0.18]] as const).forEach(([mult, amp, d]) => {
    const osc = a.createOscillator(); const g = a.createGain();
    osc.type = 'sine'; osc.frequency.setValueAtTime(freq * mult, t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol * amp, t + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t + d);
    osc.connect(g).connect(out); osc.start(t); osc.stop(t + d + 0.05);
  });
}

// "Hello DinDin" — an original 16-note tune written for the app (C major pentatonic, bouncy).
// Each tap plays the next note, so a burst of kicks becomes a little song.
const N = { G4: 392.0, A4: 440.0, C5: 523.25, D5: 587.33, E5: 659.25, G5: 783.99, A5: 880.0, C6: 1046.5 };
const TUNE = [
  N.G4, N.C5, N.E5, N.G5, N.E5, N.C5, N.D5, N.E5,
  N.A4, N.C5, N.D5, N.G5, N.A5, N.G5, N.E5, N.C5
];
let tunePos = 0;
let lastNoteAt = 0;

export const sfx = {
  setEnabled(on: boolean) { enabled = on; ambient.setEnabled(on); },
  get enabled() { return enabled; },

  /** Each kick plays the next note of the Hello DinDin tune; a pause of a few seconds starts it over. */
  kick(_n = 1) {
    ambient.duck();
    const now = Date.now();
    if (now - lastNoteAt > 4000) tunePos = 0;
    lastNoteAt = now;
    bell(TUNE[tunePos % TUNE.length]!, 0, 0.17);
    tunePos++;
  },

  /** Cheerful music-box flourish (10 reached, a note saved). */
  complete() {
    [N.C5, N.E5, N.G5, N.C6].forEach((f, i) => bell(f, i * 0.11, 0.14, 1.2));
    bell(N.E5, 0.5, 0.1, 1.6); bell(N.G5, 0.5, 0.1, 1.6); bell(N.C6, 0.5, 0.12, 1.8);
  },

  /** Quiet tine for UI confirmations. */
  tap() { bell(N.C6 * 1.5, 0, 0.06, 0.4); },

  undo() { bell(N.E5, 0, 0.1, 0.5); bell(N.C5, 0.1, 0.1, 0.7); }
};
